import React, { useEffect, useState } from 'react'
import { DS } from '@/design-system'
import { formatTimeUntilStart, formatCountdown } from '../../../utils/dateFormatter'

const COUNTDOWN_DISPLAY_THRESHOLD_MS = 120_000

interface LobbyCountdownProps {
  /** Wall-clock target for tournament start. */
  scheduledStartTime: number
}

/**
 * Self-ticking leaf that renders the "Starts in X" / "Starting soon..."
 * text for a pre-active tournament lobby.
 *
 * Owns its own 1-second interval so the parent tree doesn't re-render
 * every second just because the countdown's displayed value changes.
 * Mounts only when the lobby is in `registration` or `lobby` state;
 * unmount stops the interval automatically.
 */
export const LobbyCountdown = React.memo(function LobbyCountdown({
  scheduledStartTime
}: LobbyCountdownProps) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const timeUntilStart = scheduledStartTime - now

  if (timeUntilStart <= COUNTDOWN_DISPLAY_THRESHOLD_MS) {
    return (
      <DS.Text variant="Body Large/Medium" style={{ color: DS.palette['Brand/Maroon'] }}>
        Starting soon...
      </DS.Text>
    )
  }

  const { label, isDate } = formatTimeUntilStart(timeUntilStart, new Date(scheduledStartTime))
  return (
    <DS.Text variant="Body Large/Medium">
      {isDate ? `Starts ${label}` : `Starts in ${label}`}
    </DS.Text>
  )
})

interface LateJoinWindowProps {
  gracePeriodEndsAt: number
  /**
   * Children render while `now < gracePeriodEndsAt` AND the leaf renders the
   * accompanying "Late join available for X" countdown text above them.
   * Pass the Join Table button as children — when the window closes, both
   * the text and the button disappear together.
   */
  children: React.ReactNode
}

/**
 * Self-ticking leaf that:
 *  1. Renders "Late join available for X" with a 1-second-fresh countdown.
 *  2. Gates `children` (typically the Join Table button) on `now < gracePeriodEndsAt`.
 *
 * Owns its own 1-second interval so the parent action bar doesn't re-render
 * every second. Renders null once `gracePeriodEndsAt` has passed; the
 * surrounding tree should also remove this branch on the next status
 * update (the leaf's null state is the bridge between the local-clock
 * threshold crossing and the server's confirmation).
 */
export const LateJoinWindow = React.memo(function LateJoinWindow({
  gracePeriodEndsAt,
  children
}: LateJoinWindowProps) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const remainingMs = gracePeriodEndsAt - now
  if (remainingMs <= 0) return null

  return (
    <>
      <DS.Text variant="Body Large/Medium">
        Late join available for {formatCountdown(remainingMs)}
      </DS.Text>
      {children}
    </>
  )
})

interface GracePeriodGateProps {
  gracePeriodEndsAt: number
  /** Rendered while the grace period (late registration) is open. */
  open?: React.ReactNode
  /** Rendered once it has ended. */
  closed?: React.ReactNode
}

/**
 * Renders `open` until `gracePeriodEndsAt`, then `closed`. Nothing here shows
 * a countdown, so one timer for the single crossing replaces a per-second tick.
 */
export const GracePeriodGate = React.memo(function GracePeriodGate({
  gracePeriodEndsAt,
  open,
  closed
}: GracePeriodGateProps) {
  const [ended, setEnded] = useState(() => Date.now() >= gracePeriodEndsAt)
  useEffect(() => {
    if (ended) return
    const id = setTimeout(() => setEnded(true), Math.max(0, gracePeriodEndsAt - Date.now()))
    return () => clearTimeout(id)
  }, [ended, gracePeriodEndsAt])

  return <>{ended ? closed : open}</>
})

interface StartNowGateProps {
  /** Scheduled tournament start time (ms since epoch). */
  scheduledStartTime: number
  /** Window before scheduledStartTime during which children render. */
  windowMs: number
  children: React.ReactNode
}

/**
 * Conditionally renders `children` only when `now >= scheduledStartTime - windowMs`.
 * Self-ticking at the minute level (60Hz would be wasteful — the gate only
 * flips ONCE during the lobby lifetime). The gate's own state change is the
 * single re-render at the threshold crossing; children mount/unmount around
 * it.
 *
 * Used for the "Start Now" button which should only appear in the final
 * five minutes before scheduled start.
 */
export const StartNowGate = React.memo(function StartNowGate({
  scheduledStartTime,
  windowMs,
  children
}: StartNowGateProps) {
  // Compute the wall-clock instant at which the window opens; tick once per
  // minute to detect the crossing. Minute granularity is enough — even a
  // 30s lag at the threshold isn't user-visible.
  const [withinWindow, setWithinWindow] = useState(() => Date.now() >= scheduledStartTime - windowMs)
  useEffect(() => {
    if (withinWindow) return // Already open; no need to keep ticking.
    const id = setInterval(() => {
      if (Date.now() >= scheduledStartTime - windowMs) {
        setWithinWindow(true)
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [withinWindow, scheduledStartTime, windowMs])

  if (!withinWindow) return null
  return <>{children}</>
})
